<template>
  <DefaultLayout>
    <template #menu-prepend>
      <BtnComponent @click="toggleSearch">
        <template #prepend>
          <IconComponent icon="aip-magnifier" size="1.1rem" />
        </template>
        {{ $t('unitList.searchUnit') }}
      </BtnComponent>
    </template>
    <div>
      <div
        v-if="showSearch"
        class="mb-2 flex align-center"
      >
        <InputComponent
          ref="searchInput"
          v-model="search"
          :label="$t('unitList.searchUnit')"
          class="grow-1"
        />
        <BtnComponent
          class="mt-1"
          @click="closeSearch"
        >
          <IconComponent icon="aip-close" size="2rem" />
        </BtnComponent>
      </div>

      <DeviceList
        :units="unitList"
        :disable-dnd="showSearch"
        @on-select="openUnit"
        @on-dnd-move="onDndMove"
      />

      <div
        v-if="!$apollo.loading && !unitList.length"
        class="no-device-center-box"
      >
        <h2
          v-if="search"
        >
          {{ $t('unitList.notInList') }}
        </h2>
        <DialogComponent>
          <template #activator="{ toggle }">
            <div
              class="add-first-unit"
              @click="toggle"
            >
              <div
                v-if="unitListConfig.addUnit.deviceIcon"
                class="icon"
              >
                <IconComponent :icon="unitListConfig.addUnit.deviceIcon" color="#fff" size="6rem" />
              </div>
              <p class="title">
                {{ $t('unitList.addYourDevice') }}
              </p>
              <IconComponent icon="aip-plus-circle" size="1.4rem" color="#fff" />
            </div>
          </template>
          <template #default="{ toggle }">
            <AddUnitView @on-cancel="toggle" @on-completed="() => { toggle(); reFetch() } " />
          </template>
        </DialogComponent>
      </div>

      <div v-if="isLoading" class="text-center">
        <PlaceholderLoader
          v-for="i in 5"
          :key="i"
          :style="{ height: '3rem', opacity: .6 - i * 0.1 }"
        />
      </div>

      <div
        v-if="!search && !isDemo && !isLoading && !allLoaded"
        class="text-center pt-2 pb-5"
      >
        <BtnComponent
          large
          @click="loadMore"
        >
          {{ $t('unitList.loadMore') }}
        </BtnComponent>
      </div>
    </div>
  </DefaultLayout>
</template>

<script>
import DefaultLayout from '../layouts/DefaultLayout.vue';
import DeviceList from '@acs/components-library/src/components/deviceList/DeviceList.vue';
import BtnComponent from '@acs/components-library/src/components/common/Btn.vue';
import IconComponent from '@acs/components-library/src/components/common/Icon.vue';
import InputComponent from '@acs/components-library/src/components/common/Input.vue';
import DialogComponent from '@acs/components-library/src/components/common/Dialog.vue';
import PlaceholderLoader from '@acs/components-library/src/components/common/PlaceholderLoader.vue';

import router from '../router';
import { logout } from '@acs/components-library/src/auth.js';
import { isDemo } from '@acs/components-library/src/demo.js';
import {
  ViewQuery,
  UpdatePositionMutation,
} from '@acs/components-library/src/gql/live/UnitList.js';
import AddUnitView from './AddUnitView.vue';

import { appLabelConfig } from '@acs/components-library/src/appLabel/appLabelConfig.js';

export default {
  components: {
    PlaceholderLoader,
    AddUnitView,
    DefaultLayout,
    DialogComponent,
    DeviceList,
    BtnComponent,
    IconComponent,
    InputComponent,
  },
  beforeRouteEnter(to, from, next) {
    if (from.name === 'UnitDetail' && !isDemo()) {
      this?.$apollo.queries.units.refetch();
    }
    next();
  },
  apollo: {
    units: {
      query: ViewQuery,
      variables() {
        return {
          after: null,
          first: this.unitListConfig.unitsPerPage,
          search: this.search,
        };
      },
      skip() {
        return this.search.length > 0 && this.search.length < 3;
      },
      result({ data }) {
        if (
          this.isDemo || (data.units && data.units.units.length < this.unitListConfig.unitsPerPage)
        ) {
          this.allLoaded = true;
        }
      },
    },
  },
  data() {
    return {
      search: '',
      showSearch: false,
      isDemo: isDemo(),
      allLoaded: false,
      appLabelConfig: appLabelConfig,
      unitListConfig: appLabelConfig.poolLiveFront.unitList,
    };
  },
  computed: {
    isLoading() {
      return this.$apollo.loading;
    },
    unitList() {
      return this.units?.units || [];
    },
  },
  methods: {
    async loadMore() {
      if (this.isDemo) {
        return;
      }
      await this.$apollo.queries.units.fetchMore({
        variables: {
          after: this.units.cursor,
          first: this.unitListConfig.unitsPerPage,
          search: this.search,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const { units: previousUnits } = previousResult.units;
          const {
            units: newUnits,
            cursor: newCursor,
          } = fetchMoreResult.units;

          if (newUnits.length === 0) {
            this.allLoaded = true;
            return previousResult;
          }

          if (newUnits.length < this.unitListConfig.unitsPerPage) {
            this.allLoaded = true;
          }

          const notYetPresent = (unit) => {
            return !previousUnits.some(
              (u) => u.serialNumber === unit.serialNumber,
            );
          };

          return {
            units: {
              cursor: newCursor,
              units: [
                ...previousUnits,
                ...newUnits.filter(notYetPresent),
              ],
              __typename: 'UnitList',
            },
          };
        },
      });
    },
    async reFetch() {
      await this.$apollo.queries.units.refetch(
        {
          after: null,
          first: this.unitList.length,
          search: this.search,
        },
      );
    },
    async onDndMove(e) {
      const units = this.unitList;

      const { oldIndex, newIndex } = e;
      const sn = units[oldIndex].serialNumber;
      const newPosition = units[newIndex].position;
      // this.unitList[newIndex].position = 42;

      if (!sn || !Number.isInteger(newPosition) && newPosition < 0) {
        return;
      }

      if (this.isDemo) {
        return;
      }

      await this.$apollo.mutate({
        mutation: UpdatePositionMutation,
        variables: {
          update: {
            serialNumber: sn,
            position: newPosition,
          },
        },
      });

      await this.reFetch();
    },
    openUnit(unit) {
      router.push({ name: 'UnitDetail',  params: { 'sn': unit.serialNumber } });
    },
    async handleLogout() {
      if (this.isDemo) {
        // stopDemo();
        // router.replace('/login');
        location.assign('/login'); // fixme: clear cache hotfix
      } else {
        await logout();
        // redirection will be handled higher up in the App.vue
      }
    },
    async toggleSearch() {
      this.showSearch = !this.showSearch;
      await this.$nextTick();
      if (this.showSearch) {
        this.$refs.searchInput.$el.querySelector('input').focus();
      } else {
        this.search = '';
      }
    },
    closeSearch() {
      this.showSearch = false;
      this.search = '';
    },
  },
};
</script>
<style lang="scss">
.header-logo {
  margin-top: .1875rem;
}

.no-device-center-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem;
  gap: 1.5rem;

  .add-first-unit {
    background: var(--boxBackground);
    padding: 3rem 3rem 2.5rem;
    box-sizing: border-box;
    border-radius: calc( 3* #{ var(--borderRadius) } );
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: 1px solid transparent;

    &:hover {
      border-color: rgba(255, 255, 255, .4);
    }

    .icon {
      padding-bottom: .5rem;
    }

    .title {
      font-size: 1.2rem;
      font-weight: 300;
      text-transform: uppercase;
    }
  }
}

</style>
